import React, { useState, useEffect, useCallback } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Reports = () => {
  const { user } = useAuth();
  const [graduates, setGraduates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseLevels, setCourseLevels] = useState([]);
  const [examBodies, setExamBodies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredLevels, setFilteredLevels] = useState([]);

  // Filter state for reports
  const [filters, setFilters] = useState({
    department_id: '',
    course_id: '',
    level_id: '',
    exam_body_id: '',
    grade: '',
    start_date: '',
    end_date: ''
  });

  // Report data
  const [reportData, setReportData] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [reportTitle, setReportTitle] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [gradsRes, deptsRes, coursesRes, levelsRes, examRes] = await Promise.all([
        api.get('/graduates'),
        api.get('/departments'),
        api.get('/courses'),
        api.get('/course-levels'),
        api.get('/exam-bodies')
      ]);

      let graduatesData = gradsRes.data;
      if (user?.role === 'editor' && user?.department_id) {
        graduatesData = graduatesData.filter(g => g.department_id === parseInt(user.department_id));
        setFilters(prev => ({ ...prev, department_id: user.department_id }));
      }

      setGraduates(graduatesData);
      setDepartments(deptsRes.data);
      setCourses(coursesRes.data);
      
      // Flatten all levels from exam bodies
      const allLevels = [];
      levelsRes.data.forEach(examBody => {
        if (examBody.levels && examBody.levels.length > 0) {
          examBody.levels.forEach(level => {
            allLevels.push({
              id: level.id,
              level_number: level.level_number,
              level_name: level.level_name,
              exam_body_id: examBody.exam_body_id,
              exam_body_name: examBody.exam_body_name,
              display_name: level.level_number ? `Level ${level.level_number}` : level.level_name
            });
          });
        }
      });
      setCourseLevels(allLevels);
      setExamBodies(examRes.data);
      
      // Initialize filtered courses
      setFilteredCourses(coursesRes.data);
      setFilteredLevels(allLevels);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update filtered courses when department changes
  useEffect(() => {
    if (filters.department_id) {
      const filtered = courses.filter(c => c.department_id === parseInt(filters.department_id));
      setFilteredCourses(filtered);
      // Reset course filter when department changes
      setFilters(prev => ({ ...prev, course_id: '' }));
    } else {
      setFilteredCourses(courses);
    }
  }, [filters.department_id, courses]);

  // Update filtered levels when exam body changes
  useEffect(() => {
    if (filters.exam_body_id) {
      const filtered = courseLevels.filter(l => l.exam_body_id === parseInt(filters.exam_body_id));
      setFilteredLevels(filtered);
      // Reset level filter when exam body changes
      setFilters(prev => ({ ...prev, level_id: '' }));
    } else {
      setFilteredLevels(courseLevels);
    }
  }, [filters.exam_body_id, courseLevels]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      department_id: user?.role === 'editor' ? (user?.department_id || '') : '',
      course_id: '',
      level_id: '',
      exam_body_id: '',
      grade: '',
      start_date: '',
      end_date: ''
    });
  };

  const getLevelDisplay = (grad) => {
    if (grad.level_number) {
      return `Level ${grad.level_number}`;
    }
    if (grad.level_name) {
      return grad.level_name;
    }
    return '—';
  };

  const getCourseDisplayWithLevel = (grad) => {
    // This matches the format in Graduates.jsx
    let courseText = grad.course_name || '—';
    if (grad.level_number) {
      courseText += ` (Level ${grad.level_number})`;
    } else if (grad.level_name) {
      courseText += ` (${grad.level_name})`;
    }
    return courseText;
  };

  const getCourseDisplayForFilter = (course) => {
    // Display course with level in filter dropdown
    let displayText = course.name;
    if (course.level_number) {
      displayText += ` (Level ${course.level_number})`;
    } else if (course.level_name) {
      displayText += ` (${course.level_name})`;
    }
    return displayText;
  };

  const generateReport = () => {
    let filtered = [...graduates];

    if (filters.department_id) {
      filtered = filtered.filter(g => g.department_id === parseInt(filters.department_id));
    }
    if (filters.course_id) {
      filtered = filtered.filter(g => g.course_id === parseInt(filters.course_id));
    }
    if (filters.level_id) {
      filtered = filtered.filter(g => g.level_id === parseInt(filters.level_id));
    }
    if (filters.exam_body_id) {
      filtered = filtered.filter(g => g.exam_body_id === parseInt(filters.exam_body_id));
    }
    if (filters.grade) {
      filtered = filtered.filter(g => g.grade === filters.grade);
    }
    if (filters.start_date) {
      filtered = filtered.filter(g => new Date(g.created_at) >= new Date(filters.start_date));
    }
    if (filters.end_date) {
      filtered = filtered.filter(g => new Date(g.created_at) <= new Date(filters.end_date));
    }

    setReportData(filtered);

    const titleParts = [];
    if (filters.department_id) {
      const dept = departments.find(d => d.id === parseInt(filters.department_id));
      if (dept) titleParts.push(dept.name);
    }
    if (filters.course_id) {
      const course = courses.find(c => c.id === parseInt(filters.course_id));
      if (course) titleParts.push(getCourseDisplayForFilter(course));
    }
    if (filters.level_id) {
      const level = courseLevels.find(l => l.id === parseInt(filters.level_id));
      if (level) titleParts.push(level.display_name);
    }
    if (filters.exam_body_id) {
      const exam = examBodies.find(e => e.id === parseInt(filters.exam_body_id));
      if (exam) titleParts.push(exam.name);
    }
    if (filters.grade) {
      titleParts.push(filters.grade);
    }

    const title = titleParts.length > 0 ? `Graduates Report - ${titleParts.join(' - ')}` : 'All Graduates Report';
    setReportTitle(title);
    setShowReport(true);
  };

  const exportToExcel = async () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      const XLSX = await import('xlsx');

      const worksheetData = reportData.map((grad, index) => ({
        'S/N': index + 1,
        'Administration Number': grad.admin_number || '',
        'Full Name': grad.full_name || grad.name || '',
        'Telephone': grad.telephone || '—',
        'Email Address': grad.email || '—',
        'Department': grad.department_name || '—',
        'Course': getCourseDisplayWithLevel(grad),
        'Level': getLevelDisplay(grad),
        'Exam Body': grad.exam_body_name || '—',
        'Grade': grad.grade || '—',
        'Graduation Date': grad.ceremony_date ? new Date(grad.ceremony_date).toLocaleDateString() : '—',
        'Certificate Number': grad.certificate_number || '—',
        'Date Registered': grad.created_at ? new Date(grad.created_at).toLocaleDateString() : '—'
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();

      const colWidths = [
        { wch: 6 },   // S/N
        { wch: 18 },  // Admin Number
        { wch: 25 },  // Full Name
        { wch: 15 },  // Telephone
        { wch: 25 },  // Email
        { wch: 20 },  // Department
        { wch: 35 },  // Course
        { wch: 15 },  // Level
        { wch: 20 },  // Exam Body
        { wch: 12 },  // Grade
        { wch: 15 },  // Graduation Date
        { wch: 20 },  // Certificate Number
        { wch: 15 }   // Date Registered
      ];
      worksheet['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Graduates Report');

      const fileName = `${reportTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success(`Exported ${reportData.length} records successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              margin: 20px;
              background: white;
              font-size: 12px;
            }
            .report-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #1e3a8a;
              padding-bottom: 15px;
            }
            .institution-name {
              font-size: 20px;
              font-weight: bold;
              color: #1e3a8a;
            }
            .report-title {
              font-size: 16px;
              color: #333;
              margin: 8px 0;
            }
            .report-info {
              margin: 12px 0;
              color: #666;
              font-size: 11px;
              display: flex;
              justify-content: space-between;
              flex-wrap: wrap;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
              font-size: 10px;
            }
            th {
              background-color: #1e3a8a;
              color: white;
              padding: 8px 6px;
              text-align: left;
              font-weight: 600;
              border: 1px solid #2e4a9a;
              white-space: nowrap;
            }
            td {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
              vertical-align: top;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 9px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; padding: 8px; }
              .no-print { display: none; }
              th { background-color: #1e3a8a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <div class="institution-name">Katine Technical Training Institute</div>
            <div class="report-title">${reportTitle}</div>
          </div>
          <div class="report-info">
            <span>Generated on: ${new Date().toLocaleString()}</span>
            <span>Total Graduates: ${reportData.length}</span>
            <span>Generated by: ${user?.name || 'System Administrator'}</span>
          </div>
           <table>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Admin No</th>
                <th>Full Name</th>
                <th>Telephone</th>
                <th>Email</th>
                <th>Department</th>
                <th>Course</th>
                <th>Level</th>
                <th>Exam Body</th>
                <th>Grade</th>
                <th>Graduation Date</th>
                <th>Certificate No</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.map((grad, index) => `
                <tr>
                  <td style="text-align: center;">${index + 1}</td>
                  <td>${grad.admin_number || '—'}</td>
                  <td>${grad.full_name || grad.name || '—'}</td>
                  <td>${grad.telephone || '—'}</td>
                  <td>${grad.email || '—'}</td>
                  <td>${grad.department_name || '—'}</td>
                  <td>${getCourseDisplayWithLevel(grad)}</td>
                  <td>${getLevelDisplay(grad)}</td>
                  <td>${grad.exam_body_name || '—'}</td>
                  <td>${grad.grade || '—'}</td>
                  <td>${grad.ceremony_date ? new Date(grad.ceremony_date).toLocaleDateString() : '—'}</td>
                  <td>${grad.certificate_number || '—'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            <p>This is a computer-generated report from the Katine TTI Graduation Management System</p>
            <p>For verification, please contact the Registrar's Office</p>
          </div>
          <div class="no-print" style="text-align: center; margin-top: 15px;">
            <button onclick="window.print()" style="padding: 6px 12px; margin: 3px; cursor: pointer;">🖨️ Print</button>
            <button onclick="window.close()" style="padding: 6px 12px; margin: 3px; cursor: pointer;">✖️ Close</button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
          <p className="text-gray-600 mt-1">
            Generate and export graduation reports with comprehensive student data
          </p>
        </div>
      </div>

      {/* Filter Card */}
      <Card className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Generate Report</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Department</label>
            <select
              className="input-field w-full h-10"
              name="department_id"
              value={filters.department_id}
              onChange={handleFilterChange}
              disabled={user?.role === 'editor'}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Course</label>
            <select
              className="input-field w-full h-10"
              name="course_id"
              value={filters.course_id}
              onChange={handleFilterChange}
              disabled={filteredCourses.length === 0}
            >
              <option value="">All Courses</option>
              {filteredCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {getCourseDisplayForFilter(course)}
                </option>
              ))}
            </select>
            {filters.department_id && filteredCourses.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No courses found for this department</p>
            )}
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Exam Body</label>
            <select
              className="input-field w-full h-10"
              name="exam_body_id"
              value={filters.exam_body_id}
              onChange={handleFilterChange}
            >
              <option value="">All Exam Bodies</option>
              {examBodies.map(body => (
                <option key={body.id} value={body.id}>{body.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Level</label>
            <select
              className="input-field w-full h-10"
              name="level_id"
              value={filters.level_id}
              onChange={handleFilterChange}
              disabled={filteredLevels.length === 0}
            >
              <option value="">All Levels</option>
              {filteredLevels.map(level => (
                <option key={level.id} value={level.id}>
                  {level.display_name}
                </option>
              ))}
            </select>
            {filters.exam_body_id && filteredLevels.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No levels found for this exam body</p>
            )}
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Grade</label>
            <select
              className="input-field w-full h-10"
              name="grade"
              value={filters.grade}
              onChange={handleFilterChange}
            >
              <option value="">All Grades</option>
              <option value="Distinction">Distinction</option>
              <option value="Credit">Credit</option>
              <option value="Pass">Pass</option>
              <option value="Mastery">Mastery</option>
              <option value="Proficiency">Proficiency</option>
              <option value="Competent">Competent</option>
              <option value="First Class">First Class</option>
            </select>
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
                placeholder="Start Date"
              />
              <Input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
                placeholder="End Date"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={generateReport} disabled={loading}>
            📊 Generate Report
          </Button>
          <Button variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Report Results */}
      {showReport && (
        <Card>
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <h2 className="text-xl font-semibold text-gray-800">{reportTitle}</h2>
            <div className="flex gap-3">
              <Button onClick={exportToExcel} variant="secondary" size="sm" disabled={exporting}>
                {exporting ? 'Exporting...' : '📊 Export to Excel'}
              </Button>
              <Button onClick={printReport} variant="outline" size="sm">
                🖨️ Print Report
              </Button>
              <Button onClick={() => setShowReport(false)} variant="outline" size="sm">
                New Report
              </Button>
            </div>
          </div>

          <div className="mb-4 text-sm text-gray-600 grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
            <div><strong>Total Graduates:</strong> {reportData.length}</div>
            <div><strong>Generated on:</strong> {new Date().toLocaleString()}</div>
            <div><strong>Generated by:</strong> {user?.name || 'System'}</div>
            <div><strong>Filters Applied:</strong> {Object.values(filters).filter(v => v).length}</div>
          </div>

          {/* Horizontal Scroll Table */}
          <div className="overflow-x-auto shadow rounded-lg">
            <table className="min-w-[1300px] bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telephone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exam Body</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Graduation Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certificate No</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                      No graduates found with the selected filters.
                    </td>
                  </tr>
                ) : (
                  reportData.map((grad, index) => (
                    <tr key={grad.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 sticky left-0 bg-white text-center">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{grad.admin_number}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{grad.full_name || grad.name || '—'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{grad.telephone || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 truncate max-w-[150px]" title={grad.email}>
                          {grad.email || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{grad.department_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 whitespace-nowrap">
                          {getCourseDisplayWithLevel(grad)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          grad.level_number === 6 ? 'bg-purple-100 text-purple-800' :
                          grad.level_number === 5 ? 'bg-blue-100 text-blue-800' :
                          grad.level_number === 4 ? 'bg-green-100 text-green-800' :
                          grad.level_number === 3 ? 'bg-yellow-100 text-yellow-800' :
                          grad.level_name === 'Diploma' ? 'bg-indigo-100 text-indigo-800' :
                          grad.level_name === 'Certificate' ? 'bg-cyan-100 text-cyan-800' :
                          grad.level_name === 'Artisan' ? 'bg-orange-100 text-orange-800' :
                          grad.level_name === 'First Class' ? 'bg-amber-100 text-amber-800' :
                          grad.level_name === 'Pass' ? 'bg-lime-100 text-lime-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getLevelDisplay(grad)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{grad.exam_body_name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded ${
                          grad.grade === 'Distinction' ? 'bg-yellow-100 text-yellow-800' :
                          grad.grade === 'Credit' ? 'bg-blue-100 text-blue-800' :
                          grad.grade === 'Pass' ? 'bg-green-100 text-green-800' :
                          grad.grade === 'Mastery' ? 'bg-purple-100 text-purple-800' :
                          grad.grade === 'Proficiency' ? 'bg-indigo-100 text-indigo-800' :
                          grad.grade === 'Competent' ? 'bg-cyan-100 text-cyan-800' :
                          grad.grade === 'First Class' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {grad.grade || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {grad.ceremony_date ? new Date(grad.ceremony_date).toLocaleDateString() : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{grad.certificate_number || '—'}</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Reports;
